import { Link } from "react-router-dom";
import type { Article } from "@/mocks/types";
import { getCategory } from "@/mocks/categories";
import { formatViews, parseDate } from "@/lib/site";

export function CategoryTag({ slug, small }: { slug: string; small?: boolean }) {
  const c = getCategory(slug);
  if (!c) return null;
  return (
    <span
      className={`inline-flex items-center gap-1 whitespace-nowrap rounded-sm font-semibold ${
        small ? "px-1.5 py-0.5 text-[10px]" : "px-2 py-0.5 text-xs"
      } bg-primary-100 text-primary-700`}
    >
      <i className={c.icon}></i>
      {c.name}
    </span>
  );
}

function MetaLine({ a }: { a: Article }) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
      <span className="inline-flex items-center gap-1">
        <i className="ri-eye-line"></i>
        {formatViews(a.views)} 浏览
      </span>
      <span className="hidden sm:inline-flex items-center gap-1">
        <i className="ri-time-line"></i>
        {parseDate(a.date)} · {a.readTime}
      </span>
      <span className="inline-flex items-center gap-1 text-primary-500">
        <i className="ri-fire-line"></i>
        {formatViews(a.likes)} 热评
      </span>
    </div>
  );
}

/* 大幅横向杂志卡 */
export function MagazineCard({ a }: { a: Article }) {
  const c = getCategory(a.category);
  return (
    <Link
      to={`/article/${a.slug}`}
      className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-100 transition-colors hover:border-primary-300"
    >
      <div className="relative aspect-[16/9] overflow-hidden">
        <img
          src={a.image}
          alt={a.title}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute left-3 top-3 flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1 rounded-sm bg-background-950/70 px-2 py-1 text-[11px] font-semibold text-background-50 backdrop-blur">
            <i className={c?.icon}></i>
            {c?.source}
          </span>
        </div>
        {a.hot && (
          <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-sm bg-primary-500 px-2 py-1 text-[11px] font-semibold text-background-50">
            <i className="ri-fire-fill"></i>
            热
          </span>
        )}
      </div>
      <div className="flex flex-1 flex-col p-4 md:p-5">
        <h3 className="line-clamp-2 font-heading text-lg font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600 md:text-xl">
          {a.title}
        </h3>
        <p className="mt-2 line-clamp-2 flex-1 text-sm leading-relaxed text-foreground-600">
          {a.excerpt}
        </p>
        <div className="mt-4 border-t border-background-200 pt-3">
          <MetaLine a={a} />
        </div>
      </div>
    </Link>
  );
}

/* 竖版信息流卡 */
export function FeedCard({ a }: { a: Article }) {
  return (
    <Link
      to={`/article/${a.slug}`}
      className="group flex gap-4 rounded-lg border border-background-200 bg-background-100 p-3 transition-colors hover:border-primary-300 md:p-4"
    >
      <div className="relative aspect-square w-20 shrink-0 overflow-hidden rounded-md md:w-28">
        <img
          src={a.image}
          alt={a.title}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-center gap-2">
          <CategoryTag slug={a.category} small />
          {a.hot && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-primary-500">
              <i className="ri-fire-fill"></i>火爆
            </span>
          )}
        </div>
        <h3 className="mt-1.5 line-clamp-2 font-heading text-base font-bold leading-snug text-foreground-900 transition-colors group-hover:text-primary-600">
          {a.title}
        </h3>
        <div className="mt-auto pt-2">
          <MetaLine a={a} />
        </div>
      </div>
    </Link>
  );
}

/* 热瓜榜编号项 */
export function RankItem({ a, index }: { a: Article; index: number }) {
  const top = index < 3;
  return (
    <Link
      to={`/article/${a.slug}`}
      className="group flex items-start gap-3 border-b border-background-200 py-3 transition-colors last:border-0"
    >
      <span
        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md font-heading text-sm font-extrabold ${
          top
            ? "bg-primary-500 text-background-50"
            : "bg-secondary-200 text-secondary-900"
        }`}
      >
        {index + 1}
      </span>
      <div className="min-w-0">
        <p className="line-clamp-2 text-sm font-medium leading-snug text-foreground-800 transition-colors group-hover:text-primary-600">
          {a.title}
        </p>
        <p className="mt-1 flex items-center gap-1.5 text-xs text-foreground-400">
          <i className="ri-eye-line"></i>
          {formatViews(a.views)}
        </p>
      </div>
    </Link>
  );
}